/*
 * WaWiLagerApp.tsx
 *
 * Created by Dr. Maximillian Dornseif 2022-01-30 in huWaWi3 24.14.0
 * Copyright (c) 2022 HUDORA GmbH
 */

import gql from 'graphql-tag'
import React from 'react'

import {
  AUFTRAG_FRAGMENT,
  BESTANDSBUCHSUMME_FRAGMENT,
  BESTANDSBUCH_FRAGMENT,
  BESTELLPOSITION_FRAGMENT,
  KOMMI_FRAGMENT,
  LAGER_FRAGMENT,
  WERTKORREKTUR_FRAGMENT,
  ZUTEILUNG_FRAGMENT,
} from '../fragments'
import { IEntitySubPage } from '../lib/components/EntityPage'
import { KindListe } from '../lib/components/KindListe'
import { KindPage } from '../lib/components/KindPage'
import { IcLagerKorrekturbuchung } from './IcLagerKorrekturbuchung'

export function IcLagerApp(props) {
  const subPages: IEntitySubPage[] = [
    {
      title: 'Aufträge',
      path: 'a',
      component: (
        <KindListe
          schemaName={'HwAuftrag'}
          listQuery={AUFTRAG_LIST_QUERY}
          hideExcept={[
            'designator',
            'auftragsnr',
            'status',
            'auftragsnr_kunde',
            'anlieferdatum_bis',
            'anlieferdatum_von',
            'created_via',
            'erfasst_von',
            'created_at',
          ]}
          title={''}
        />
      ),
    },
    {
      title: 'Bestände',
      path: 'bestaende',
      component: (
        <KindListe
          schemaName={'HwIcBestandsbuchSumme'}
          listQuery={BESTAND_LIST_QUERY}
          title={''}
          hideExcept={[
            'lager',
            'sku',
            'artnr',
            'lager_menge',
            'lager_wert',
            'adw',
            'zugangsmenge',
            'abgangsmenge',
            'bewegungen',
            'erster_zugang',
            'letzter_abgang',
          ]}
        />
      ),
    },
    {
      title: 'Bestellungen',
      path: 'bestellungen',
      component: <KindListe schemaName={'HwBestellPosition'} listQuery={BESTELL_LIST_QUERY} title={''} />,
    },

    {
      title: 'Kommis',
      path: 'kommi',
      component: <KindListe schemaName={'HwKommiAuftrag2'} listQuery={KOMMI_LIST_QUERY} title={''} />,
    },

    {
      title: 'Zuteilungen',
      path: 'zuteilungen',
      component: <KindListe schemaName={'HwIcZuteilung'} listQuery={ZUTEILUNG_LIST_QUERY} title={''} />,
    },
    {
      title: 'Wertkorrekturen',
      path: 'korrekturen',
      component: (
        <KindListe schemaName={'HwIcWertkorrektur'} listQuery={WERTKORREKTUR_LIST_QUERY} title={''} />
      ),
    },
    {
      title: 'Korrekturbuchung erfassen',
      path: 'korrektur',
      component: <IcLagerKorrekturbuchung />,
    },
    {
      title: 'Bestandsbuch',
      path: 'bestandsbuch',
      component: (
        <KindListe
          schemaName={'HwIcBestandsbuchEintrag'}
          listQuery={BESTANDSBUCH_LIST_QUERY}
          title={''}
          hideExcept={[
            'buchungsnr',
            'datum',
            'lager',
            'menge',
            'artnr',
            'menge_nach_verbuchung',
            'ausloeser',
            'vorgang',
            'vorgang_posnr',
            'charge',
            'beleg',
          ]}
        />
      ),
    },
  ]

  return (
    <KindPage
      basePath={'/ic'}
      componentPath={'lager'}
      schemaName={'HwLager'}
      title={'Läger'}
      subPages={subPages}
      listQuery={LAGER_LIST_QUERY}
      entityQuery={LAGER_ENTITY_QUERY}
      editMutation={LAGER_EDIT_MUTATION}
    ></KindPage>
  )
}
const LAGER_LIST_QUERY = gql`
  query AlleLagerPages($cursor: String) {
    nodes: hwLagerPages(after: $cursor, sort: { fields: [updated_at], order: DESC }) {
      edges {
        cursor
        node {
          ...lagerFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${LAGER_FRAGMENT}
`
const LAGER_ENTITY_QUERY = gql`
  query EinLager($nr: ID!) {
    entity: hwLager(designator: $nr) {
      ...lagerFragment
    }
  }
  ${LAGER_FRAGMENT}
`
const LAGER_EDIT_MUTATION = gql`
  mutation LagerEditMutation($designator: ID!, $input: HwLagerInput!) {
    entity: hwLagerUpdate(designator: $designator, input: $input) {
      ...lagerFragment
    }
  }
  ${LAGER_FRAGMENT}
`

export const BESTAND_LIST_QUERY = gql`
  query LagerBestandQuery($nr: ID!) {
    entity: hwLager(designator: $nr) {
      id
      nodes: bestaende(sort: { fields: lager_wert, order: DESC }, limit: 5000) {
        ...bestandsbuchsummeFragment
      }
    }
  }
  ${BESTANDSBUCHSUMME_FRAGMENT}
`
export const AUFTRAG_LIST_QUERY = gql`
  query LagerAuftraege($nr: ID!) {
    entity: hwLager(designator: $nr) {
      id
      nodes: auftraege(filter: { erledigt: { eq: false } }) {
        ...auftragFragment
      }
    }
  }
  ${AUFTRAG_FRAGMENT}
`

export const BESTELL_LIST_QUERY = gql`
  query LBQuery($nr: ID!) {
    entity: hwLager(designator: $nr) {
      id
      nodes: bestellPositionList(filter: { erledigt: { eq: false } }) {
        ...bestellpositionFragment
      }
    }
  }
  ${BESTELLPOSITION_FRAGMENT}
`

export const KOMMI_LIST_QUERY = gql`
  query LKommiAuftraege($nr: ID!) {
    entity: hwLager(designator: $nr) {
      id
      nodes: kommiauftragList(
        filter: { status: { lt: "600rechnung_erzeugt" } }
        sort: { fields: [status, updated_at], order: [ASC, DESC] }
      ) {
        ...kommiAuftragFragment
      }
    }
  }
  ${KOMMI_FRAGMENT}
`

export const BESTANDSBUCH_LIST_QUERY = gql`
  query LBBQuery($nr: ID!) {
    entity: hwLager(designator: $nr) {
      id
      nodes: icBestandsbuchEintragList {
        ...bestandsbuchFragment
      }
    }
  }
  ${BESTANDSBUCH_FRAGMENT}
`

export const ZUTEILUNG_LIST_QUERY = gql`
  query LZQuery($nr: ID!) {
    entity: hwLager(designator: $nr) {
      id
      nodes: icZuteilungList {
        ...zuteilungFragment
      }
    }
  }
  ${ZUTEILUNG_FRAGMENT}
`
export const WERTKORREKTUR_LIST_QUERY = gql`
  query LWQuery($nr: ID!) {
    entity: hwLager(designator: $nr) {
      id
      nodes: icWertkorrekturList {
        ...wertkorrekturFragment
      }
    }
  }
  ${WERTKORREKTUR_FRAGMENT}
`
